import React, { useEffect, useState } from "react";
import TopNav from "@/components/TopNav";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UnoCardView } from "@/components/UnoCard";
import SbCardView from "@/components/SkipBoCard";
import { LOADOUT } from "@/constants/testIds";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const authHeader = () => {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const UNO_PREV = { color: "red", value: "5" };
const SB_PREV = { id: "p", value: 7 };

export default function Loadout() {
  const { user, setUser } = useAuth();
  const [data, setData] = useState({ owned_uno_skins: [], owned_sb_skins: [], owned_backgrounds: [], active_uno_skin: "classic", active_sb_skin: "sb_classic", active_background: "bg_default" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/loadout", { headers: authHeader() });
      setData(r.data);
    } catch (e) {}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const equip = async (id) => {
    try {
      const r = await api.post(`/store/skins/${id}/equip`, {}, { headers: authHeader() });
      setUser(r.data.user);
      toast.success("Loadout updated");
      load();
    } catch (e) { toast.error("Could not equip"); }
  };

  return (
    <div className="min-h-screen" data-testid={LOADOUT.container}>
      <TopNav/>
      <main className="max-w-6xl mx-auto px-4 sm:px-10 py-6 sm:py-10 relative z-10">
        <div className="mb-6 sm:mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-2">Loadout</div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tighter">Choose your set.</h1>
            <p className="text-white/60 mt-2 text-sm max-w-lg">Pick which card designs you want to play with — one for Uno and one for Skip-Bo. Preview updates instantly.</p>
          </div>
          <Link to="/store" className="text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full bg-[color:var(--uno-yellow)] text-black font-semibold">+ Buy more skins</Link>
        </div>

        {loading && <div className="text-white/50">Loading…</div>}

        {/* Uno section */}
        <section className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-2.5 h-2.5 rounded-full" style={{background:"var(--uno-red)"}}/>
            <div className="font-display text-2xl">Uno set</div>
            <div className="text-xs text-white/40 uppercase tracking-widest ml-auto">{data.owned_uno_skins.length} owned</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {data.owned_uno_skins.map(skin => {
              const active = skin.id === data.active_uno_skin;
              return (
                <button
                  key={skin.id}
                  data-testid={`${LOADOUT.selectUnoPrefix}${skin.id}`}
                  onClick={() => equip(skin.id)}
                  className={`rounded-xl p-3 border transition-all ${active ? "border-[color:var(--uno-yellow)] bg-white/5" : "border-white/10 hover:border-white/30 bg-black/20"}`}
                >
                  <div className="flex justify-center py-2">
                    <UnoCardView card={UNO_PREV} size="sm" skin={skin.id}/>
                  </div>
                  <div className="mt-2 text-sm font-semibold">{skin.name}</div>
                  {active && <div className="text-[10px] uppercase tracking-widest text-[color:var(--uno-yellow)] mt-1">Equipped</div>}
                </button>
              );
            })}
          </div>
        </section>

        {/* Skip-Bo section */}
        <section className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-2.5 h-2.5 rounded-full" style={{background:"var(--uno-blue)"}}/>
            <div className="font-display text-2xl">Skip-Bo set</div>
            <div className="text-xs text-white/40 uppercase tracking-widest ml-auto">{data.owned_sb_skins.length} owned</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {data.owned_sb_skins.map(skin => {
              const active = skin.id === data.active_sb_skin;
              return (
                <button
                  key={skin.id}
                  data-testid={`${LOADOUT.selectSbPrefix}${skin.id}`}
                  onClick={() => equip(skin.id)}
                  className={`rounded-xl p-3 border transition-all ${active ? "border-[color:var(--uno-yellow)] bg-white/5" : "border-white/10 hover:border-white/30 bg-black/20"}`}
                >
                  <div className="flex justify-center py-2">
                    <SbCardView card={SB_PREV} size="sm" skin={skin.id}/>
                  </div>
                  <div className="mt-2 text-sm font-semibold">{skin.name}</div>
                  {active && <div className="text-[10px] uppercase tracking-widest text-[color:var(--uno-yellow)] mt-1">Equipped</div>}
                </button>
              );
            })}
          </div>
        </section>

        {/* Backgrounds */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-2.5 h-2.5 rounded-full" style={{background:"var(--uno-green)"}}/>
            <div className="font-display text-2xl">Background</div>
            <div className="text-xs text-white/40 uppercase tracking-widest ml-auto">{(data.owned_backgrounds || []).length} owned</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {(data.owned_backgrounds || []).map(skin => {
              const active = skin.id === data.active_background;
              return (
                <button
                  key={skin.id}
                  data-testid={`loadout-select-bg-${skin.id}`}
                  onClick={() => equip(skin.id)}
                  className={`rounded-xl p-2 border transition-all ${active ? "border-[color:var(--uno-yellow)] bg-white/5" : "border-white/10 hover:border-white/30 bg-black/20"}`}
                >
                  <div className={`bg-${skin.id} bg-preview w-full h-24 rounded-lg border border-white/10 overflow-hidden`}/>
                  <div className="mt-2 text-sm font-semibold">{skin.name}</div>
                  {active && <div className="text-[10px] uppercase tracking-widest text-[color:var(--uno-yellow)] mt-1">Equipped</div>}
                </button>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}
